import { useState, useEffect, useCallback, useRef } from 'react'
import { useQuery } from '@tanstack/react-query'
import api from 'api/api'
import { useEventBus } from './useEventBus'
import { Subscription } from 'rxjs'
import { filter } from 'rxjs/operators'

export interface TemporalWorkflowProgress {
    message: string
    timestamp: number
}

export interface TemporalWorkflowStatus {
    status: 'running' | 'completed' | 'failed'
    workflow_id: string
    run_id: string
    query_name: string
    workflow_class: string
    result?: TemporalWorkflowProgress[]
    error?: string
}

export interface UseTemporalWorkflowStatusOptions {
    enabled: boolean
    pollInterval?: number
    onComplete?: (result: any) => void
    onError?: (error: string) => void
    eventBusTopic?: string // EventBus topic for real-time updates
    completionKeywords?: string[] // Keywords that indicate workflow completion
    logPrefix?: string // Prefix for console logging
}

export function useTemporalWorkflowStatus(
    workflowId: string | null,
    runId: string | null,
    queryName: string = 'progress',
    workflowClass: string,
    options: UseTemporalWorkflowStatusOptions = { enabled: false },
) {
    const [status, setStatus] = useState<TemporalWorkflowStatus | null>(null)
    const [isLoading, setIsLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [isPolling, setIsPolling] = useState(false)
    const [eventBusEnabled, setEventBusEnabled] = useState(true)
    const eventBusSubscription = useRef<Subscription | null>(null)

    const {
        enabled,
        pollInterval = 1000,
        onComplete,
        onError,
        eventBusTopic,
        completionKeywords = ['successfully!', 'completed'],
        logPrefix = 'Workflow',
    } = options

    const fetchStatus = useCallback(async () => {
        if (!workflowId || !runId || !enabled) {
            return
        }

        try {
            setIsLoading(true)
            setError(null)

            const response = await api.getTemporalWorkflowStatus(
                workflowId,
                runId,
                queryName,
                workflowClass,
            )

            setStatus(response)

            // Show only the latest progress message
            const progress = response.result || []
            const lastMessage = progress[progress.length - 1]

            if (lastMessage?.message) {
                console.log(`${logPrefix}:`, lastMessage.message)

                // Check if message contains any completion keywords
                const isCompleted = completionKeywords.some(keyword =>
                    lastMessage.message.toLowerCase().includes(keyword.toLowerCase()),
                )

                if (isCompleted) {
                    setIsPolling(false)
                    onComplete?.(response)
                }
            }

            return response
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred'

            // Don't stop polling for timeout errors - let react-query handle retries
            const isTimeoutError =
                errorMessage.includes('query timed out') ||
                errorMessage.includes('timeout') ||
                errorMessage.includes('DEADLINE_EXCEEDED')

            if (!isTimeoutError) {
                // Only stop polling for non-timeout errors
                setError(errorMessage)
                setIsPolling(false)
                onError?.(errorMessage)
            } else {
                // For timeout errors, just log but continue polling
                console.log('Temporal query timeout, will retry automatically...')
            }

            throw err
        } finally {
            setIsLoading(false)
        }
    }, [
        workflowId,
        runId,
        queryName,
        workflowClass,
        enabled,
        onComplete,
        onError,
        completionKeywords,
        logPrefix,
    ])

    const startPolling = useCallback(() => {
        if (!workflowId || !runId || isPolling) {
            return
        }
        setIsPolling(true)
        setError(null)
    }, [workflowId, runId, isPolling])

    const stopPolling = useCallback(() => {
        setIsPolling(false)
    }, [])

    // Old WebSocket approach removed - now using EventBus for real-time updates

    // Use EventBus for real-time updates (new approach)
    const { getTopic$, isConnected: isEventBusConnected } = useEventBus()

    useEffect(() => {
        // Only use EventBus if topic is provided and EventBus is enabled
        if (!enabled || !workflowId || !runId || !eventBusEnabled || !eventBusTopic) {
            return
        }

        // Wait for EventBus connection
        if (!isEventBusConnected) {
            return
        }

        // Subscribe to the specified EventBus topic
        eventBusSubscription.current = getTopic$(eventBusTopic)
            .pipe(
                filter(
                    (payload: any) =>
                        payload.workflow_id === workflowId && payload.run_id === runId,
                ),
            )
            .subscribe({
                next: payload => {
                    // Status message already logged in eventBusService

                    // Update local state with EventBus data
                    setStatus(prev => ({
                        ...prev,
                        status: payload.status.completed ? 'completed' : 'running',
                        workflow_id: payload.workflow_id,
                        run_id: payload.run_id,
                        query_name: queryName,
                        workflow_class: workflowClass,
                        result: [...(prev?.result || []), payload.status],
                    }))

                    // Check for completion using generic status.completed flag
                    if (payload.status.completed) {
                        setIsPolling(false)
                        const finalStatus = {
                            status: 'completed' as const,
                            workflow_id: payload.workflow_id,
                            run_id: payload.run_id,
                            query_name: queryName,
                            workflow_class: workflowClass,
                            result: [...(status?.result || []), payload.status],
                        }
                        setStatus(finalStatus)
                        onComplete?.(finalStatus)
                    }
                },
                error: err => {
                    console.error(
                        '❌ [useTemporalWorkflowStatus] EventBus subscription error:',
                        err,
                    )
                    setEventBusEnabled(false)
                    // Don't call onError here as it's just a transport failure, not a workflow error
                },
            })

        return () => {
            if (eventBusSubscription.current) {
                eventBusSubscription.current.unsubscribe()
                eventBusSubscription.current = null
            }
        }
    }, [
        enabled,
        workflowId,
        runId,
        workflowClass,
        eventBusEnabled,
        isEventBusConnected,
        getTopic$,
        queryName,
        onComplete,
        status,
        eventBusTopic,
    ])

    // Use react-query for polling as fallback (when WebSocket not available or fails)
    useQuery({
        queryKey: [
            'temporal-workflow-status',
            workflowId,
            runId,
            queryName,
            workflowClass,
            isPolling,
        ],
        queryFn: fetchStatus,
        enabled: !!(
            isPolling &&
            enabled &&
            workflowId &&
            runId &&
            (!eventBusEnabled || !isEventBusConnected)
        ),
        refetchInterval: eventBusEnabled && isEventBusConnected ? 5000 : 500, // Slower polling when EventBus is active
        refetchOnMount: false,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        retry: (failureCount, error) => {
            // Retry temporal timeout errors up to 3 times, but not other errors
            if (failureCount < 3) {
                const errorMessage = error instanceof Error ? error.message : String(error)
                if (
                    errorMessage.includes('query timed out') ||
                    errorMessage.includes('timeout') ||
                    errorMessage.includes('DEADLINE_EXCEEDED')
                ) {
                    console.log(
                        `Temporal query timeout (attempt ${failureCount + 1}/3), retrying...`,
                    )
                    return true
                }
            }
            return false
        },
        retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 3000), // Exponential backoff: 1s, 2s, 3s
    })

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            setIsPolling(false)
            if (eventBusSubscription.current) {
                eventBusSubscription.current.unsubscribe()
                eventBusSubscription.current = null
            }
        }
    }, [])

    return {
        status,
        isLoading,
        error,
        isPolling,
        startPolling,
        stopPolling,
        fetchStatus,
        progress: status?.result || [],
        latestMessage: status?.result?.[status.result.length - 1]?.message || null,
    }
}
